import React, { useState } from 'react';
import { motion, useInView } from 'framer-motion';
import './MonitGPS.css';
import { useLanguage } from './LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import T from './translations';

// Update these two constants to repurpose this page for a future special event.
const EVENT_NAME = 'Rajd Legend 2026';
const EVENT_LOGO = 'https://prl.org.pl/wp-content/uploads/2026/05/logo.png';

const ScrollReveal = ({ children, delay = 0 }) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const Event = () => {
  const { language } = useLanguage();
  const TT = T[language];
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', montaz: '', srednicaRury: '', nip: '', firma: '', adres: '', miasto: '', message: ''
  });
  const [chceFakture, setChceFakture] = useState(false);
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        montaz: formData.montaz,
        ...(formData.montaz === 'do-klatki' && { srednicaRury: formData.srednicaRury }),
        message: formData.message,
        type: 'event',
        event: EVENT_NAME,
        faktura: chceFakture ? 'tak' : 'nie',
        ...(chceFakture && {
          nip: formData.nip,
          firma: formData.firma,
          adres: formData.adres,
          miasto: formData.miasto,
        })
      };
      const response = await fetch('/api/send_order.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', montaz: '', srednicaRury: '', nip: '', firma: '', adres: '', miasto: '', message: '' });
      } else {
        setStatus('error');
        setErrorMessage(TT.orderKit.errorText);
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage(TT.orderKit.errorConn);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden">
      {/* Navigation */}
      <motion.nav
        className="fixed top-0 left-0 right-0 z-50 glass-effect"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <a href="/">
              <img src={EVENT_LOGO} alt={EVENT_NAME} className="h-12 w-auto cursor-pointer" />
            </a>
          </div>
          <div className="hidden md:flex items-center space-x-8 text-sm font-medium">
            <a href="/" className="hover:text-cyan-400 transition-colors">{TT.nav.home}</a>
            <a href="/zawodnik" className="hover:text-cyan-400 transition-colors">{TT.nav.competitorZone}</a>
            <a href="/elearning" className="hover:text-cyan-400 transition-colors text-cyan-500 font-bold">{TT.nav.elearning}</a>
            <LanguageSwitcher />
          </div>
          <div className="md:hidden flex items-center space-x-3">
            <LanguageSwitcher />
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-white hover:text-cyan-400 focus:outline-none">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#0a0a0a]/95 border-b border-white/10 flex flex-col px-6 py-4 space-y-4 text-sm font-medium backdrop-blur-xl">
            <a href="/" className="hover:text-cyan-400">{TT.nav.home}</a>
            <a href="/zawodnik" className="hover:text-cyan-400">{TT.nav.competitorZone}</a>
            <a href="/elearning" className="text-cyan-500 font-bold hover:text-cyan-400">{TT.nav.elearning}</a>
          </div>
        )}
      </motion.nav>

      {/* Hero Header */}
      <section className="relative pt-32 pb-16 flex items-center justify-center overflow-hidden bg-carbon min-h-[50vh]">
        <div className="absolute inset-0 grid-pattern opacity-30"></div>
        <motion.div
          className="absolute top-1/2 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-[100px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <motion.div
            className="mb-8 flex justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img src={EVENT_LOGO} alt={EVENT_NAME} className="h-24 md:h-32 w-auto object-contain" />
          </motion.div>
          <motion.h1
            className="font-display font-black text-5xl md:text-7xl leading-tight mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {TT.event.heroTitle1} <span className="text-gradient">{TT.event.heroTitle2}</span>
          </motion.h1>
          <motion.p
            className="text-xl text-gray-400 max-w-2xl mx-auto font-light leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {TT.event.heroSubtitle}
          </motion.p>
          <motion.div
            className="mt-10 flex justify-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <a href="https://elearning.monitgps.pl" target="_blank" rel="noreferrer" className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl font-display font-bold text-lg hover:shadow-lg hover:shadow-cyan-500/50 transition-all flex items-center space-x-3 group">
              <span>{TT.event.elearningBtn}</span>
              <span className="text-2xl group-hover:scale-110 transition-transform">🎓</span>
            </a>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-50 scan-line"></div>
      </section>

      {/* PDF Downloads Section */}
      <section id="downloads" className="py-24 px-6 relative">
        <div className="max-w-5xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 glass-effect rounded-full text-sm font-medium text-cyan-400 mb-6">
                {TT.downloads.sectionBadge}
              </div>
              <h2 className="font-display font-black text-4xl md:text-5xl mb-6">
                {TT.downloads.sectionTitle1} <span className="text-gradient">{TT.downloads.sectionTitle2}</span>
              </h2>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8">
            <ScrollReveal delay={0.1}>
              <div className="glass-effect rounded-3xl p-8 hover-lift flex flex-col items-center text-center">
                <div className="text-6xl mb-6">📄</div>
                <h3 className="font-display font-bold text-2xl mb-4">{TT.downloads.userManualTitle}</h3>
                <p className="text-gray-400 mb-8 max-w-sm">{TT.downloads.userManualDescRally}</p>
                <a
                  href={language === 'en' ? '/downloads/ENG/Competitor-Instructions_rally_ENG.pdf' : '/downloads/Instrukcja-Zawodnika_rally.pdf'}
                  download
                  target="_blank"
                  rel="noreferrer"
                  className="w-full px-6 py-4 bg-white/5 border border-white/20 rounded-xl font-display font-bold text-white hover:bg-white/10 hover:border-cyan-500/50 transition-all flex items-center justify-center space-x-2 relative z-10"
                >
                  <span>{TT.downloads.downloadBtn}</span>
                  <span className="text-xl">⬇</span>
                </a>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <div className="glass-effect rounded-3xl p-8 hover-lift flex flex-col items-center text-center">
                <div className="text-6xl mb-6">🔧</div>
                <h3 className="font-display font-bold text-2xl mb-4">{TT.downloads.installationTitle}</h3>
                <p className="text-gray-400 mb-8 max-w-sm">{TT.downloads.installationDescRally}</p>
                <a
                  href={language === 'en' ? '/downloads/ENG/Installation-Manual_ENG.pdf' : '/downloads/Instrukcja-Instalacji_rally.pdf'}
                  download
                  target="_blank"
                  rel="noreferrer"
                  className="w-full px-6 py-4 bg-white/5 border border-white/20 rounded-xl font-display font-bold text-white hover:bg-white/10 hover:border-cyan-500/50 transition-all flex items-center justify-center space-x-2 relative z-10"
                >
                  <span>{TT.downloads.downloadBtn}</span>
                  <span className="text-xl">⬇</span>
                </a>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Order Installation Kit Section */}
      <section id="order-kit" className="py-24 px-6 relative bg-gradient-to-b from-transparent to-[#050505]">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 glass-effect rounded-full text-sm font-medium text-cyan-400 mb-6">
                {TT.orderKit.sectionBadge}
              </div>
              <h2 className="font-display font-black text-4xl md:text-5xl mb-6">
                {TT.orderKit.sectionTitle1} <span className="text-gradient">{TT.orderKit.sectionTitle2}</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">{TT.orderKit.subtitleEvent}</p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Kit Details */}
            <ScrollReveal delay={0.2}>
              <div className="glass-effect rounded-3xl p-8 h-full flex flex-col relative overflow-hidden group">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>

                <div className="mb-6 relative z-10 flex flex-col space-y-4">
                  <div>
                    <h3 className="font-display font-bold text-2xl text-cyan-400 mb-2">{TT.orderKit.pricingTitle}</h3>
                    <p className="text-gray-400 text-sm">{TT.orderKit.pricingSubtitle}</p>
                  </div>
                  <div className="flex flex-row flex-wrap gap-3 w-full">
                    <div className="flex-1 min-w-[140px] bg-blue-500/10 border border-blue-500/20 px-4 py-3 rounded-xl text-center">
                      <span className="block text-[10px] text-gray-400 uppercase tracking-widest mb-1 font-bold">{TT.orderKit.rental}</span>
                      <strong className="text-2xl text-white font-display">150 zł</strong>
                      <span className="text-blue-400 ml-1 text-sm font-bold">{TT.orderKit.rentalUnitRally}</span>
                    </div>
                    <div className="flex-1 min-w-[140px] bg-cyan-500/10 border border-cyan-500/20 px-4 py-3 rounded-xl text-center">
                      <span className="block text-[10px] text-gray-400 uppercase tracking-widest mb-1 font-bold">{TT.orderKit.kitPrice}</span>
                      <strong className="text-2xl text-white font-display">350 zł</strong>
                      <span className="text-cyan-400 ml-1 text-sm font-bold">{TT.orderKit.kitUnit}</span>
                    </div>
                  </div>
                </div>

                {/* 3 Images Grid */}
                <div className="grid grid-cols-3 gap-3 mb-8 relative z-10">
                  {[1, 2, 3].map(n => (
                    <div key={n} className="aspect-square rounded-xl bg-black/40 border border-white/5 overflow-hidden flex items-center justify-center text-2xl">
                      <img src={`/images/kit${n}.jpg`} alt={`Kit ${n}`} className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity" onError={(e) => { e.target.style.display = 'none'; }} />
                      <span className="absolute z-[-1]">📷</span>
                    </div>
                  ))}
                </div>

                {/* Product List */}
                <div className="flex-grow flex flex-col justify-start relative z-10">
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-3 flex-shrink-0">✓</span>
                      <strong className="text-white block text-lg">{TT.orderKit.antenna}</strong>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-3 mt-1 flex-shrink-0">✓</span>
                      <div>
                        <div className="flex items-center flex-wrap gap-3">
                          <strong className="text-white block text-lg">{TT.orderKit.ramMounts}</strong>
                          <div className="bg-white/10 px-2 py-1 rounded-md flex items-center justify-center">
                            <img src="/images/ram-logo.png" alt="RAM Mounts" className="h-5 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                          </div>
                        </div>
                        <div className="text-cyan-400/80 text-sm mt-1">
                          <span className="font-medium block">{TT.orderKit.ramOriginal}</span>
                          <ul className="list-none mt-2 ml-1 text-gray-300 space-y-2">
                            <li className="flex flex-col">
                              <div className="flex items-start mb-1">
                                <span className="mr-2 text-cyan-500">•</span>
                                <span><strong>{TT.orderKit.vehicleMount}</strong></span>
                              </div>
                              <div className="flex flex-col pl-6">
                                <div className="flex items-start">
                                  <span className="mr-2 text-gray-500">-</span>
                                  <span>{TT.orderKit.bodyMount}</span>
                                </div>
                                <div className="my-1 text-cyan-400 font-bold">--- OR ---</div>
                                <div className="flex items-start">
                                  <span className="mr-2 text-gray-500">-</span>
                                  <span>{TT.orderKit.cageMount}</span>
                                </div>
                                <ul className="list-[circle] list-inside pl-6 mt-1 text-gray-300 text-xs space-y-1">
                                  <li>A: 32 - 35 mm</li>
                                  <li>B: 38 - 41 mm</li>
                                  <li>C: 48 - 51 mm</li>
                                </ul>
                              </div>
                            </li>
                            <li className="flex items-start">
                              <span className="mr-2 text-cyan-500">•</span>
                              <span>{TT.orderKit.connector}</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-400 mr-3 flex-shrink-0">✓</span>
                      <strong className="text-white block text-lg">{TT.orderKit.powerCable}</strong>
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>

            {/* Order Form */}
            <ScrollReveal delay={0.4}>
              <div className="glass-effect rounded-3xl p-8 relative">
                <h3 className="font-display font-bold text-3xl mb-6 text-white text-center">
                  {TT.orderKit.formTitle}
                </h3>

                {status === 'success' ? (
                  <div className="flex flex-col items-center justify-center text-center py-12 space-y-4">
                    <div className="w-20 h-20 rounded-full bg-green-500/20 border border-green-500/50 flex items-center justify-center text-green-400 text-4xl mb-4">✓</div>
                    <h4 className="text-3xl font-bold font-display text-white">{TT.orderKit.successTitle}</h4>
                    <p className="text-gray-400 mt-2">{TT.orderKit.successText}</p>
                  </div>
                ) : (
                  <form className="space-y-5" onSubmit={handleSubmit}>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">{TT.orderKit.nameLabel}</label>
                      <input type="text" name="name" required value={formData.name} onChange={handleInputChange}
                        className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-colors text-white"
                        placeholder={TT.orderKit.namePlaceholder} />
                    </div>

                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">{TT.orderKit.emailLabel}</label>
                        <input type="email" name="email" required value={formData.email} onChange={handleInputChange}
                          className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-colors text-white"
                          placeholder={TT.orderKit.emailPlaceholder} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">{TT.orderKit.phoneLabel}</label>
                        <input type="tel" name="phone" required value={formData.phone} onChange={handleInputChange}
                          className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-colors text-white"
                          placeholder={TT.orderKit.phonePlaceholder} />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">{TT.orderKit.mountTypeLabel}</label>
                      <select name="montaz" required value={formData.montaz} onChange={handleInputChange}
                        className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-colors text-white appearance-none cursor-pointer"
                        style={{ background: 'rgba(255,255,255,0.05)' }}>
                        <option value="" disabled style={{ background: '#1a1a1a' }}>{TT.orderKit.mountTypePlaceholder}</option>
                        <option value="standardowy" style={{ background: '#1a1a1a' }}>{TT.orderKit.mountStandard}</option>
                        <option value="do-klatki" style={{ background: '#1a1a1a' }}>{TT.orderKit.mountToCage}</option>
                      </select>
                    </div>

                    {formData.montaz === 'do-klatki' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">{TT.orderKit.cageDiameterLabel}</label>
                        <select name="srednicaRury" required value={formData.srednicaRury} onChange={handleInputChange}
                          className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-colors text-white appearance-none cursor-pointer"
                          style={{ background: 'rgba(255,255,255,0.05)' }}>
                          <option value="" disabled style={{ background: '#1a1a1a' }}>{TT.orderKit.cageDiameterPlaceholder}</option>
                          <option value="A - 32-35 mm" style={{ background: '#1a1a1a' }}>A - 32-35 mm</option>
                          <option value="B - 38-41 mm" style={{ background: '#1a1a1a' }}>B - 38 - 41 mm</option>
                          <option value="C - 48-51 mm" style={{ background: '#1a1a1a' }}>C - 48 - 51 mm</option>
                          <option value={TT.orderKit.cageDiameterOther} style={{ background: '#1a1a1a' }}>{TT.orderKit.cageDiameterOther}</option>
                        </select>
                      </div>
                    )}

                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-400">{TT.orderKit.invoiceQuestion}</label>
                      <div className="flex gap-3">
                        <button type="button" onClick={() => setChceFakture(false)}
                          className="flex-1 py-3 rounded-xl font-display font-bold text-sm transition-all border"
                          style={!chceFakture
                            ? { background: 'rgba(6,182,212,0.15)', borderColor: 'rgba(6,182,212,0.6)', color: '#22d3ee' }
                            : { background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.1)', color: '#6b7280' }}>
                          {TT.orderKit.invoiceNo}
                        </button>
                        <button type="button" onClick={() => setChceFakture(true)}
                          className="flex-1 py-3 rounded-xl font-display font-bold text-sm transition-all border"
                          style={chceFakture
                            ? { background: 'rgba(6,182,212,0.15)', borderColor: 'rgba(6,182,212,0.6)', color: '#22d3ee' }
                            : { background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.1)', color: '#6b7280' }}>
                          {TT.orderKit.invoiceYes}
                        </button>
                      </div>
                    </div>

                    {chceFakture && (
                      <div className="space-y-4 p-5 rounded-xl" style={{ background: 'rgba(6,182,212,0.04)', border: '1px solid rgba(6,182,212,0.15)' }}>
                        <p className="text-xs text-cyan-400/70 font-medium uppercase tracking-wider">{TT.orderKit.invoiceTitle}</p>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">{TT.orderKit.nipLabel}</label>
                            <input type="text" name="nip" value={formData.nip} onChange={handleInputChange}
                              className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-colors text-white"
                              placeholder={TT.orderKit.nipPlaceholder} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-2">{TT.orderKit.companyLabel}</label>
                            <input type="text" name="firma" value={formData.firma} onChange={handleInputChange}
                              className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-colors text-white"
                              placeholder={TT.orderKit.companyPlaceholder} />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">{TT.orderKit.addressLabel}</label>
                          <input type="text" name="adres" value={formData.adres} onChange={handleInputChange}
                            className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-colors text-white"
                            placeholder={TT.orderKit.addressPlaceholder} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">{TT.orderKit.cityLabel}</label>
                          <input type="text" name="miasto" value={formData.miasto} onChange={handleInputChange}
                            className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-colors text-white"
                            placeholder={TT.orderKit.cityPlaceholder} />
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">{TT.orderKit.notesLabelRally}</label>
                      <textarea rows="3" name="message" value={formData.message} onChange={handleInputChange}
                        className="w-full px-5 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-colors text-white resize-none"
                        placeholder={TT.orderKit.notesPlaceholder}></textarea>
                    </div>

                    {errorMessage && (
                      <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-xl text-red-500 text-sm font-medium mt-4 mb-4">
                        {errorMessage}
                      </div>
                    )}

                    <button type="submit" disabled={status === 'loading'}
                      className="w-full py-4 mt-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl font-display font-bold text-lg hover:shadow-lg hover:shadow-cyan-500/50 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">
                      <span>{status === 'loading' ? TT.orderKit.sendingBtn : TT.orderKit.submitBtn}</span>
                      <span>✉️</span>
                    </button>
                    <p className="text-xs text-center text-gray-500 mt-3">{TT.orderKit.formNote}</p>
                  </form>
                )}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 relative overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 grid-pattern opacity-10"></div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <ScrollReveal>
            <h2 className="font-display font-black text-4xl mb-8">{TT.contactSection.title}</h2>
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="glass-effect rounded-2xl p-6">
                <div className="font-display font-bold text-lg mb-2 text-cyan-400">Maciek</div>
                <a href="tel:504178476" className="text-gray-300 hover:text-cyan-400 transition-colors">504 178 476</a>
              </div>
              <div className="glass-effect rounded-2xl p-6">
                <div className="font-display font-bold text-lg mb-2 text-cyan-400">Adrian</div>
                <a href="tel:791807484" className="text-gray-300 hover:text-cyan-400 transition-colors">791 807 484</a>
              </div>
              <div className="glass-effect rounded-2xl p-6">
                <div className="font-display font-bold text-lg mb-2 text-cyan-400">Dawid</div>
                <a href="tel:603197643" className="text-gray-300 hover:text-cyan-400 transition-colors">603 197 643</a>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 glass-effect border-t border-white/5 mt-20">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between text-sm text-gray-500">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <p>{TT.footer.copyright}</p>
            <a href="/polityka-prywatnosci" className="hover:text-cyan-400 transition-colors underline decoration-white/20 underline-offset-4">
              {TT.footer.privacyPolicy}
            </a>
          </div>
          <div className="mt-4 md:mt-0">
            <p>{TT.footer.tagline}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Event;
